import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, BookOpen, ListTree, ScrollText, Scale, TrendingUp, Landmark, Receipt, PenLine, DoorOpen, FileOutput, Waves, CalendarCheck } from 'lucide-react';
import { CashFlowView, JournalEntryView, OpeningBalancesView, TallyExportView, YearEndCloseView } from '../../components/crm/billing/BookkeepingExtras';
import Tabs from '../../components/ui/Tabs';
import PageHeader from '../../components/PageHeader';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currency';
import { useCompanyCurrency } from '../../hooks/useCompanySettings';

const TABS = [
  { key: 'daybook', label: 'Day Book', icon: BookOpen },
  { key: 'expenses', label: 'Record Expense', icon: Receipt },
  { key: 'journal', label: 'Journal Entry', icon: PenLine },
  { key: 'ledger', label: 'Ledger', icon: ScrollText },
  { key: 'trial-balance', label: 'Trial Balance', icon: Scale },
  { key: 'profit-loss', label: 'Profit & Loss', icon: TrendingUp },
  { key: 'balance-sheet', label: 'Balance Sheet', icon: Landmark },
  { key: 'cash-flow', label: 'Cash Flow', icon: Waves },
  { key: 'accounts', label: 'Chart of Accounts', icon: ListTree },
  { key: 'opening', label: 'Opening Balances', icon: DoorOpen },
  { key: 'tally', label: 'Tally Export', icon: FileOutput },
  { key: 'year-end', label: 'Year-end Close', icon: CalendarCheck },
];

const ACCOUNT_TYPE_LABELS = {
  asset: 'Asset', liability: 'Liability', equity: 'Equity',
  revenue: 'Revenue', contra_revenue: 'Contra Revenue', expense: 'Expense',
};

export default function BookkeepingPage() {
  const [tab, setTab] = useState('daybook');
  const [accounts, setAccounts] = useState([]);
  const currency = useCompanyCurrency();

  const loadAccounts = useCallback(() => {
    api.get('/crm/bookkeeping/accounts').then(res => setAccounts(res.data.accounts || [])).catch(() => {});
  }, []);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  return (
    <div className="w-full pb-20 space-y-4">
      <PageHeader title="Bookkeeping" icon={BookOpen} subtitle="Double-entry ledger — every invoice sent, payment received, refund and void is posted here as a balanced journal entry." />

      <Tabs tabs={TABS} value={tab} onChange={setTab} />

      {tab === 'daybook' && <DayBookView currency={currency} />}
      {tab === 'expenses' && <RecordExpenseView accounts={accounts} currency={currency} onRecorded={loadAccounts} />}
      {tab === 'ledger' && <LedgerView accounts={accounts} currency={currency} />}
      {tab === 'trial-balance' && <TrialBalanceView currency={currency} />}
      {tab === 'profit-loss' && <ProfitAndLossView currency={currency} />}
      {tab === 'balance-sheet' && <BalanceSheetView currency={currency} />}
      {tab === 'accounts' && <AccountsView accounts={accounts} onChanged={loadAccounts} />}
      {tab === 'journal' && <JournalEntryView accounts={accounts} currency={currency} />}
      {tab === 'opening' && <OpeningBalancesView currency={currency} />}
      {tab === 'tally' && <TallyExportView />}
      {tab === 'cash-flow' && <CashFlowView currency={currency} />}
      {tab === 'year-end' && <YearEndCloseView currency={currency} />}
    </div>
  );
}

function DayBookView({ currency }) {
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/crm/bookkeeping/day-book', { params: { date } })
      .then(res => setEntries(res.data.entries || []))
      .finally(() => setLoading(false));
  }, [date]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          className="h-9 px-2 border border-[#E5E5E5] rounded-lg text-sm" />
        <button onClick={load} disabled={loading} className="h-9 px-3 border border-[#E5E5E5] rounded text-xs font-semibold text-[#5C5C5C] hover:bg-[#F7F9FB] disabled:opacity-50 flex items-center gap-1.5">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>
      {entries.length === 0 ? (
        <p className="text-sm text-[#706E6B] py-8 text-center">{loading ? 'Loading…' : 'No journal entries for this day.'}</p>
      ) : (
        <div className="overflow-x-auto space-y-3">
          {entries.map(entry => (
            <div key={entry._id} className="border border-[#F3F2F2] rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-[#2E2E2E]">{entry.memo}</p>
                <span className="text-xs text-[#706E6B] uppercase font-medium">{entry.sourceType}</span>
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {entry.lines.map((line, i) => (
                    <tr key={i} className="border-t border-[#F3F2F2]">
                      <td className="py-1.5 text-[#5C5C5C]">{line.accountId?.code} — {line.accountId?.name}</td>
                      <td className="py-1.5 text-right w-32 text-[#2E2E2E]">{line.debit > 0 ? formatCurrency(line.debit, currency) : ''}</td>
                      <td className="py-1.5 text-right w-32 text-[#2E2E2E]">{line.credit > 0 ? formatCurrency(line.credit, currency) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LedgerView({ accounts, currency }) {
  const [accountId, setAccountId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!accountId && accounts.length) setAccountId(accounts[0]._id);
  }, [accounts, accountId]);

  useEffect(() => {
    if (!accountId) return;
    setLoading(true);
    api.get(`/crm/bookkeeping/ledger/${accountId}`).then(res => setRows(res.data.rows || [])).finally(() => setLoading(false));
  }, [accountId]);

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-4">
      <select value={accountId} onChange={e => setAccountId(e.target.value)} className="h-9 px-2 border border-[#E5E5E5] rounded-lg text-sm">
        {accounts.map(a => <option key={a._id} value={a._id}>{a.code} — {a.name}</option>)}
      </select>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#F3F2F2]">
              <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Date</th>
              <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Memo</th>
              <th className="text-right py-2 pr-4 font-semibold text-[#706E6B]">Debit</th>
              <th className="text-right py-2 pr-4 font-semibold text-[#706E6B]">Credit</th>
              <th className="text-right py-2 pr-4 font-semibold text-[#706E6B]">Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F3F2F2]">
            {rows.map((r, i) => (
              <tr key={i}>
                <td className="py-2 pr-4 text-xs text-[#706E6B]">{new Date(r.date).toLocaleDateString()}</td>
                <td className="py-2 pr-4 text-[#444444]">{r.memo}</td>
                <td className="py-2 pr-4 text-right text-[#2E2E2E]">{r.debit > 0 ? formatCurrency(r.debit, currency) : ''}</td>
                <td className="py-2 pr-4 text-right text-[#2E2E2E]">{r.credit > 0 ? formatCurrency(r.credit, currency) : ''}</td>
                <td className="py-2 pr-4 text-right font-semibold text-[#080707]">{formatCurrency(r.displayBalance ?? r.balance, currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && rows.length === 0 && <p className="text-sm text-[#706E6B] py-8 text-center">No activity on this account yet.</p>}
      </div>
    </div>
  );
}

function TrialBalanceView({ currency }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/crm/bookkeeping/trial-balance').then(res => setData(res.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <span className={cn('text-xs font-semibold px-2.5 py-1 rounded-full', data?.balanced ? 'bg-[#E3F3E8] text-[#036B3A]' : 'bg-[#FDE7E9] text-[#8E030F]')}>
          {loading ? 'Checking…' : data?.balanced ? 'Balanced' : 'Out of balance'}
        </span>
        <button onClick={load} className="h-9 px-3 border border-[#E5E5E5] rounded text-xs font-semibold text-[#5C5C5C] hover:bg-[#F7F9FB]">Refresh</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#F3F2F2]">
              <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Account</th>
              <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Type</th>
              <th className="text-right py-2 pr-4 font-semibold text-[#706E6B]">Debit</th>
              <th className="text-right py-2 pr-4 font-semibold text-[#706E6B]">Credit</th>
              <th className="text-right py-2 pr-4 font-semibold text-[#706E6B]">Balance</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F3F2F2]">
            {(data?.rows || []).map(r => (
              <tr key={r.accountId}>
                <td className="py-2 pr-4 text-[#2E2E2E] font-medium">{r.code} — {r.name}</td>
                <td className="py-2 pr-4 text-[#706E6B]">{ACCOUNT_TYPE_LABELS[r.type] || r.type}</td>
                <td className="py-2 pr-4 text-right text-[#2E2E2E]">{formatCurrency(r.debit, currency)}</td>
                <td className="py-2 pr-4 text-right text-[#2E2E2E]">{formatCurrency(r.credit, currency)}</td>
                <td className="py-2 pr-4 text-right font-semibold text-[#080707]">{formatCurrency(r.balance, currency)}</td>
              </tr>
            ))}
          </tbody>
          {data && (
            <tfoot>
              <tr className="border-t-2 border-[#E5E5E5] font-bold">
                <td className="py-2 pr-4" colSpan={2}>Total</td>
                <td className="py-2 pr-4 text-right">{formatCurrency(data.totalDebit, currency)}</td>
                <td className="py-2 pr-4 text-right">{formatCurrency(data.totalCredit, currency)}</td>
                <td className="py-2 pr-4"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

function ProfitAndLossView({ currency }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/crm/bookkeeping/profit-loss', { params: { from: from || undefined, to: to || undefined } })
      .then(res => setData(res.data))
      .finally(() => setLoading(false));
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const renderRows = rows => (rows || []).map(r => (
    <tr key={r.accountId}>
      <td className="py-2 pr-4 text-[#444444]">{r.code} — {r.name}</td>
      <td className="py-2 pr-4 text-right text-[#2E2E2E]">{formatCurrency(r.amount, currency)}</td>
    </tr>
  ));

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-4">
      <div className="flex items-center gap-2">
        <input type="date" value={from} onChange={e => setFrom(e.target.value)} placeholder="From" className="h-9 px-2 border border-[#E5E5E5] rounded-lg text-sm" />
        <input type="date" value={to} onChange={e => setTo(e.target.value)} placeholder="To" className="h-9 px-2 border border-[#E5E5E5] rounded-lg text-sm" />
        <button onClick={load} disabled={loading} className="h-9 px-3 border border-[#E5E5E5] rounded text-xs font-semibold text-[#5C5C5C] hover:bg-[#F7F9FB] disabled:opacity-50 flex items-center gap-1.5">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
        <span className="text-xs text-[#706E6B] ml-auto">Leave dates blank for all-time</span>
      </div>

      {data && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody className="divide-y divide-[#F3F2F2]">
              <tr><td colSpan={2} className="py-2 pr-4 font-bold text-[#080707]">Revenue</td></tr>
              {renderRows(data.revenue)}
              <tr className="border-t border-[#F3F2F2] font-semibold"><td className="py-2 pr-4">Gross Revenue</td><td className="py-2 pr-4 text-right">{formatCurrency(data.grossRevenue, currency)}</td></tr>

              <tr><td colSpan={2} className="py-2 pr-4 font-bold text-[#080707] pt-4">Less: Refunds</td></tr>
              {renderRows(data.refunds)}
              <tr className="border-t border-[#F3F2F2] font-semibold"><td className="py-2 pr-4">Net Revenue</td><td className="py-2 pr-4 text-right">{formatCurrency(data.netRevenue, currency)}</td></tr>

              <tr><td colSpan={2} className="py-2 pr-4 font-bold text-[#080707] pt-4">Expenses</td></tr>
              {data.expenses?.length ? renderRows(data.expenses) : (
                <tr><td colSpan={2} className="py-2 pr-4 text-[#706E6B] text-xs">No expense accounts posted yet.</td></tr>
              )}
              <tr className="border-t border-[#F3F2F2] font-semibold"><td className="py-2 pr-4">Total Expenses</td><td className="py-2 pr-4 text-right">{formatCurrency(data.totalExpenses, currency)}</td></tr>

              <tr className="border-t-2 border-[#E5E5E5] font-bold text-base">
                <td className="py-3 pr-4">Net Income</td>
                <td className={cn('py-3 pr-4 text-right', data.netIncome < 0 ? 'text-[#BA0517]' : 'text-[#036B3A]')}>{formatCurrency(data.netIncome, currency)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BalanceSheetView({ currency }) {
  const [asOf, setAsOf] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/crm/bookkeeping/balance-sheet', { params: { asOf: asOf || undefined } })
      .then(res => setData(res.data))
      .finally(() => setLoading(false));
  }, [asOf]);

  useEffect(() => { load(); }, [load]);

  const renderRows = rows => (rows || []).map(r => (
    <tr key={r.accountId}>
      <td className="py-2 pr-4 text-[#444444]">{r.code} — {r.name}</td>
      <td className="py-2 pr-4 text-right text-[#2E2E2E]">{formatCurrency(r.amount, currency)}</td>
    </tr>
  ));

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-4">
      <div className="flex items-center gap-2">
        <input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} className="h-9 px-2 border border-[#E5E5E5] rounded-lg text-sm" />
        <button onClick={load} disabled={loading} className="h-9 px-3 border border-[#E5E5E5] rounded text-xs font-semibold text-[#5C5C5C] hover:bg-[#F7F9FB] disabled:opacity-50 flex items-center gap-1.5">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
        {data && (
          <span className={cn('text-xs font-semibold px-2.5 py-1 rounded-full ml-auto', data.balanced ? 'bg-[#E3F3E8] text-[#036B3A]' : 'bg-[#FDE7E9] text-[#8E030F]')}>
            {data.balanced ? 'Balanced' : 'Out of balance'}
          </span>
        )}
      </div>

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-[#F3F2F2]">
                <tr><td colSpan={2} className="py-2 pr-4 font-bold text-[#080707]">Assets</td></tr>
                {renderRows(data.assets)}
                <tr className="border-t-2 border-[#E5E5E5] font-bold"><td className="py-2.5 pr-4">Total Assets</td><td className="py-2.5 pr-4 text-right">{formatCurrency(data.totalAssets, currency)}</td></tr>
              </tbody>
            </table>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-[#F3F2F2]">
                <tr><td colSpan={2} className="py-2 pr-4 font-bold text-[#080707]">Liabilities</td></tr>
                {renderRows(data.liabilities)}
                <tr className="border-t border-[#F3F2F2] font-semibold"><td className="py-2 pr-4">Total Liabilities</td><td className="py-2 pr-4 text-right">{formatCurrency(data.totalLiabilities, currency)}</td></tr>

                <tr><td colSpan={2} className="py-2 pr-4 font-bold text-[#080707] pt-4">Equity</td></tr>
                {renderRows(data.equity)}
                <tr>
                  <td className="py-2 pr-4 text-[#444444]">Current Period Earnings</td>
                  <td className="py-2 pr-4 text-right text-[#2E2E2E]">{formatCurrency(data.currentEarnings, currency)}</td>
                </tr>
                <tr className="border-t border-[#F3F2F2] font-semibold"><td className="py-2 pr-4">Total Equity</td><td className="py-2 pr-4 text-right">{formatCurrency(data.totalEquity, currency)}</td></tr>

                <tr className="border-t-2 border-[#E5E5E5] font-bold"><td className="py-2.5 pr-4">Total Liabilities + Equity</td><td className="py-2.5 pr-4 text-right">{formatCurrency(data.totalLiabilities + data.totalEquity, currency)}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function RecordExpenseView({ accounts, currency, onRecorded }) {
  const expenseAccounts = accounts.filter(a => a.type === 'expense');
  const paymentAccounts = accounts.filter(a => ['1010', '2000'].includes(a.code));

  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    amount: '',
    expenseAccountId: '',
    paidFromAccountId: '',
    memo: '',
  });
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);

  useEffect(() => {
    setForm(f => ({
      ...f,
      expenseAccountId: f.expenseAccountId || expenseAccounts[0]?._id || '',
      paidFromAccountId: f.paidFromAccountId || paymentAccounts.find(a => a.code === '1010')?._id || '',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accounts.length]);

  const loadRecent = useCallback(() => {
    setLoadingRecent(true);
    api.get('/crm/bookkeeping/day-book', { params: { date: new Date().toISOString().slice(0, 10) } })
      .then(res => setRecent((res.data.entries || []).filter(e => e.sourceType === 'manual')))
      .finally(() => setLoadingRecent(false));
  }, []);

  useEffect(() => { loadRecent(); }, [loadRecent]);

  const submit = async e => {
    e.preventDefault();
    const amount = Number(form.amount);
    if (!amount || amount <= 0) {
      return alertService.error('Error', 'Enter a valid expense amount');
    }
    if (!form.expenseAccountId || !form.paidFromAccountId) {
      return alertService.error('Error', 'Select an expense category and a payment account');
    }
    setSaving(true);
    try {
      await api.post('/crm/bookkeeping/journal', {
        date: form.date,
        memo: form.memo || 'Expense recorded',
        currency,
        lines: [
          { accountId: form.expenseAccountId, debit: amount, credit: 0 },
          { accountId: form.paidFromAccountId, debit: 0, credit: amount },
        ],
      });
      alertService.success('Recorded', 'Expense posted to the ledger');
      setForm(f => ({ ...f, amount: '', memo: '' }));
      loadRecent();
      onRecorded();
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to record expense');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-6">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input type="date" required value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
            className="h-9 px-2 border border-[#E5E5E5] rounded-lg text-sm" />
          <input type="number" min="0.01" step="0.01" required placeholder={`Amount (${currency})`}
            value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
            className="h-9 px-2 border border-[#E5E5E5] rounded-lg text-sm" />
          <select required value={form.expenseAccountId} onChange={e => setForm({ ...form, expenseAccountId: e.target.value })}
            className="h-9 px-2 border border-[#E5E5E5] rounded-lg text-sm">
            <option value="" disabled>Expense category…</option>
            {expenseAccounts.map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
          </select>
          <select required value={form.paidFromAccountId} onChange={e => setForm({ ...form, paidFromAccountId: e.target.value })}
            className="h-9 px-2 border border-[#E5E5E5] rounded-lg text-sm">
            <option value="" disabled>Paid from…</option>
            {paymentAccounts.map(a => <option key={a._id} value={a._id}>{a.name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <input placeholder="Description (optional)" value={form.memo} onChange={e => setForm({ ...form, memo: e.target.value })}
            className="h-9 px-2 border border-[#E5E5E5] rounded-lg text-sm flex-1" />
          <button type="submit" disabled={saving} className="h-9 px-4 bg-[#0176D3] text-white rounded text-xs font-semibold hover:bg-[#014486] disabled:opacity-50 flex items-center gap-1.5 shrink-0">
            <Plus size={14} /> {saving ? 'Recording…' : 'Record Expense'}
          </button>
        </div>
        {expenseAccounts.length === 0 && (
          <p className="text-xs text-[#706E6B]">No expense categories yet — add one under "Chart of Accounts" (type: Expense) first.</p>
        )}
      </form>

      <div>
        <h3 className="text-sm font-semibold text-[#2E2E2E] mb-2">Today's Expenses</h3>
        {loadingRecent ? (
          <p className="text-sm text-[#706E6B] py-4 text-center">Loading…</p>
        ) : recent.length === 0 ? (
          <p className="text-sm text-[#706E6B] py-4 text-center">No manual entries recorded today.</p>
        ) : (
          <div className="overflow-x-auto space-y-2">
            {recent.map(entry => (
              <div key={entry._id} className="flex items-center justify-between border border-[#F3F2F2] rounded-lg p-3 text-sm">
                <span className="text-[#444444]">{entry.memo}</span>
                <span className="font-semibold text-[#080707]">
                  {formatCurrency(entry.lines.reduce((s, l) => s + (l.debit || 0), 0), currency)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AccountsView({ accounts, onChanged }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', name: '', type: 'asset', description: '' });
  const [saving, setSaving] = useState(false);

  const submit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/crm/bookkeeping/accounts', form);
      setForm({ code: '', name: '', type: 'asset', description: '' });
      setShowForm(false);
      onChanged();
    } catch (err) {
      alertService.error('Error', err.response?.data?.message || 'Failed to create account');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] rounded-lg p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[#2E2E2E]">Accounts</h3>
        <button onClick={() => setShowForm(s => !s)} className="h-9 px-3 bg-[#0176D3] text-white rounded-lg text-xs font-semibold hover:bg-[#014486] flex items-center gap-1.5">
          <Plus size={14} /> New Account
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-4 gap-3 p-3 bg-[#F7F9FB] rounded-lg">
          <input required placeholder="Code (e.g. 5000)" value={form.code} onChange={e => setForm({ ...form, code: e.target.value })}
            className="h-9 px-2 border border-[#E5E5E5] rounded-lg text-sm" />
          <input required placeholder="Name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
            className="h-9 px-2 border border-[#E5E5E5] rounded-lg text-sm" />
          <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} className="h-9 px-2 border border-[#E5E5E5] rounded-lg text-sm">
            {Object.entries(ACCOUNT_TYPE_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          <button type="submit" disabled={saving} className="h-9 px-3 bg-[#0176D3] text-white rounded text-xs font-semibold hover:bg-[#014486] disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </form>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#F3F2F2]">
              <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Code</th>
              <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Name</th>
              <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Type</th>
              <th className="text-left py-2 pr-4 font-semibold text-[#706E6B]">Source</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F3F2F2]">
            {accounts.map(a => (
              <tr key={a._id}>
                <td className="py-2 pr-4 font-mono text-xs text-[#5C5C5C]">{a.code}</td>
                <td className="py-2 pr-4 text-[#2E2E2E] font-medium">{a.name}</td>
                <td className="py-2 pr-4 text-[#706E6B]">{ACCOUNT_TYPE_LABELS[a.type] || a.type}</td>
                <td className="py-2 pr-4">
                  <span className={cn('text-xs px-2 py-0.5 rounded-full font-semibold', a.isSystem ? 'bg-[#EAF4FE] text-[#014486]' : 'bg-[#F3F2F2] text-[#5C5C5C]')}>
                    {a.isSystem ? 'System' : 'Custom'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
